import { Component, type ErrorInfo, type ReactNode } from 'react'
import { translate, type TranslationKey } from '@/i18n/dictionary'
import { getActiveLocale } from '@/i18n/locale'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { isChunkLoadFailure } from '@/lib/lazyRoute'
import { Button, Card } from './ui'

interface Props {
  children: ReactNode
  /** Changing this resets the boundary — pass the route so navigating recovers. */
  resetKey?: string
}

interface State {
  error: Error | null
  info: ErrorInfo | null
}

/**
 * Catches a render crash in one screen instead of losing the whole app.
 *
 * Without this, a single bad property access white-screens everything — which
 * is precisely what happened when the admin activity log read `meta.last_page`
 * on a response that had no `meta`. The user saw a blank page with the real
 * cause only in the browser console.
 *
 * Must be a class: `componentDidCatch` has no hook equivalent, and adding a
 * dependency for one small component is not worth it.
 */
/**
 * Translated outside React's tree, because this is a class component and there
 * is no hook to reach for — the same reason `lib/api.ts` does it this way for
 * its interceptors.
 *
 * The consequence is that a crash screen already on display will not relabel
 * itself if the language is switched behind it. That is the right trade: this
 * screen exists because the render tree is broken, and re-subscribing it to a
 * context is exactly the kind of thing that fails a second time.
 */
const t = (key: TranslationKey) => translate(getActiveLocale(), key)

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ info })

    // Kept in the console so the stack is still there for a developer, and so
    // whatever error reporting gets wired up later has one obvious place to hook.
    console.error('[KaProLean] Render error:', error, info.componentStack)
  }

  componentDidUpdate(previous: Props): void {
    // Navigating away from the broken screen should clear it, otherwise the
    // user is stuck on the error page until they reload.
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, info: null })
    }
  }

  render(): ReactNode {
    const { error, info } = this.state

    if (!error) return this.props.children

    /*
     * A chunk that would not load is not a crash, and saying "the error was
     * recorded" about one is worse than saying nothing — it sends somebody
     * hunting for a bug in a screen that is fine.
     *
     * By the time this renders, `lazyRoute` has already spent its one reload,
     * so the build really is unreachable: the device is offline, or a proxy is
     * eating the asset. Reloading again is still the right thing to offer, and
     * it is what the retry button does here rather than merely clearing the
     * boundary — nothing about re-rendering would fetch the file again.
     */
    const staleBuild = isChunkLoadFailure(error)

    return (
      <div className="mx-auto max-w-xl py-8">
        <Card className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-6 shrink-0 text-rose-600" aria-hidden />
            <div>
              <h1 className="font-semibold">
                {t(staleBuild ? 'error.staleBuild.title' : 'error.boundary.title')}
              </h1>
              <p className="muted mt-1 text-sm">
                {t(staleBuild ? 'error.staleBuild.body' : 'error.boundary.body')}
              </p>
            </div>
          </div>

          {/*
            The message is shown, not hidden behind "an error occurred".
            Half the people using this are the developer or an administrator,
            and "meta is undefined" is the difference between a useful bug
            report and "it broke".
          */}
          <details className="rounded-xl bg-[var(--surface-sunken)] p-3">
            <summary className="cursor-pointer text-sm font-medium">{t('error.technicalDetails')}</summary>
            <p className="mt-2 font-mono text-xs break-words">{error.message}</p>
            {info?.componentStack && (
              <pre className="muted mt-2 max-h-48 overflow-auto text-[11px] leading-relaxed whitespace-pre-wrap">
                {info.componentStack.trim()}
              </pre>
            )}
          </details>

          <div className="flex gap-2">
            <Button
              onClick={() =>
                staleBuild
                  ? window.location.reload()
                  : this.setState({ error: null, info: null })
              }
            >
              <RefreshCw className="size-4" aria-hidden />
              {t(staleBuild ? 'error.staleBuild.reload' : 'action.retry')}
            </Button>
            <Button variant="secondary" onClick={() => (window.location.href = '/')}>
              {t('error.goHome')}
            </Button>
          </div>
        </Card>
      </div>
    )
  }
}
